import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, Loader2, Check, X, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const ResetTeamPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const orgSlug = searchParams.get('org');
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resultData, setResultData] = useState(null);
  const navigate = useNavigate();

  const isPasswordValid = 
    password.length >= 8 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password);

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/team-members/reset-password`, {
        token,
        password
      });

      if (response.data.success) {
        setSuccess(true);
        setResultData(response.data);
        toast.success('Password reset successfully!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMsg);
      
      // If token is invalid/expired, show helpful message
      if (error.response?.status === 404 || error.response?.status === 400) {
        toast.error('Your reset link may have expired. Please request a new one.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
          <CardHeader className="text-center pt-16 pb-6">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg" style={{background: '#10B981'}}>
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
              Password Reset!
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Your password has been updated successfully
            </p>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800 mb-2">
                ✅ You can now log in with your new password
              </p>
              {resultData?.email && (
                <p className="text-xs text-green-700">
                  Email: <strong>{resultData.email}</strong>
                </p>
              )}
              {(resultData?.organizationSlug || orgSlug) && (
                <p className="text-xs text-green-700 mt-1">
                  Organization: <strong>{resultData?.organizationSlug || orgSlug}</strong>
                </p>
              )}
            </div>

            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 text-base font-semibold"
              style={{background: '#2563EB'}}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </CardContent>
          
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} BookingFlows. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // No token state
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
          <CardHeader className="text-center pt-16 pb-6">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl shadow-lg" style={{background: '#EF4444'}}>
                <X className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
              Invalid Link
            </CardTitle>
            <p className="text-gray-600 mt-2">
              This password reset link is invalid or expired
            </p>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 mb-2">
                ❌ No reset token found
              </p>
              <p className="text-xs text-red-700">
                Please request a new password reset link from your profile settings.
              </p>
            </div>

            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 text-base font-semibold"
              style={{background: '#2563EB'}}
            >
              Back to Login
            </Button>
          </CardContent>
          
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} BookingFlows. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Password reset form
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        <CardHeader className="text-center pt-16 pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#2563EB'}}>
              <Lock className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            Reset Your Password
          </CardTitle>
          <p className="text-gray-600 mt-2">
            Create a new secure password for your account
          </p>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                New Password *
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="h-12 text-base border border-gray-200 focus:border-blue-500"
                autoFocus
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-700">
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                className="h-12 text-base border border-gray-200 focus:border-blue-500"
                data-testid="confirm-password-input"
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
              {passwordsMatch && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Passwords match
                </p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
              
              <div className="flex items-center gap-2">
                {password.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${password.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>8+ characters</span>
              </div>

              <div className="flex items-center gap-2">
                {/[a-z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[a-z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 lowercase letter</span>
              </div>

              <div className="flex items-center gap-2">
                {/[A-Z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[A-Z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 uppercase letter</span>
              </div>

              <div className="flex items-center gap-2">
                {/[0-9]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[0-9]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 number</span>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold mt-6"
              style={{background: '#2563EB'}}
              disabled={loading || !isPasswordValid || !passwordsMatch}
              data-testid="reset-password-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Resetting Password...
                </span>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ResetTeamPassword;
